import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { customerService } from '../../services/customerService';
import { 
  FiSave, FiX, FiUser, FiPhone, FiMail, FiMapPin, 
  FiCheckCircle, FiXCircle, FiAlertCircle, FiFileText,
  FiHome, FiUsers, FiBriefcase
} from 'react-icons/fi';

const CustomerForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = !!id;

  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    address: '',
    type: 'perusahaan',
    status: 'active',
    notes: ''
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const customerTypes = [
    { value: 'perusahaan', label: 'Perusahaan', icon: FiBriefcase },
    { value: 'personal', label: 'Personal / Individu', icon: FiUser },
    { value: 'instansi', label: 'Instansi Pemerintah', icon: FiHome },
    { value: 'others', label: 'Lainnya', icon: FiUsers }
  ];

  const customerStatuses = [
    { value: 'active', label: 'Active', icon: FiCheckCircle, color: 'text-green-600' },
    { value: 'inactive', label: 'Inactive', icon: FiXCircle, color: 'text-red-600' },
    { value: 'blocked', label: 'Blocked', icon: FiAlertCircle, color: 'text-gray-600' }
  ];

  useEffect(() => {
    if (isEdit) {
      loadCustomer();
    }
  }, [id]);

  const loadCustomer = async () => { 
    try {
      const customer = await customerService.getById(id); 
      if (customer) {
        setFormData({
          name: customer.name || '',
          phone: customer.phone || '',
          email: customer.email || '',
          address: customer.address || '',
          type: customer.type || 'perusahaan',
          status: customer.status || 'active',
          notes: customer.notes || ''
        });
      } else {
        setError('Customer not found');
      }
    } catch (error) {
      console.error('Error loading customer:', error);
      setError('Gagal memuat data customer');
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const validateForm = () => {
    if (!formData.name.trim()) {
      setError('Nama customer wajib diisi');
      return false;
    }
    if (!formData.phone.trim()) {
      setError('Nomor telepon wajib diisi');
      return false;
    }
    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      setError('Format email tidak valid');
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    if (!validateForm()) return;

    setLoading(true);
    try {
      if (isEdit) {
        await customerService.update(id, formData);
      } else {
        await customerService.create(formData);
      }
      navigate('/customers');
    } catch (error) {
      console.error('Error saving customer:', error);
      // Tampilkan pesan error yang jelas
      const errorMessage = error?.message || error?.toString() || 'Terjadi kesalahan saat menyimpan';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-[var(--text-primary)]">
          {isEdit ? 'Edit Customer' : 'New Customer'}
        </h1>
        <button
          onClick={() => navigate('/customers')}
          className="flex items-center space-x-2 px-4 py-2 bg-gray-500 hover:bg-gray-600 text-white rounded-lg transition-colors"
        >
          <FiX size={20} />
          <span>Cancel</span>
        </button>
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-100 text-red-700 rounded-lg">
          ❌ {error}
        </div>
      )}

      <form onSubmit={handleSubmit} className="bg-[var(--bg-primary)] p-6 rounded-xl shadow-[var(--shadow-sm)] border border-[var(--border-color)]">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Nama Customer */}
          <div>
            <label className="block text-sm font-medium text-[var(--text-secondary)] mb-1">
              Nama Customer *
            </label>
            <div className="relative">
              <FiUser className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-secondary)]" size={18} />
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                placeholder="Contoh: PT. Bangun Tidur"
                className="w-full pl-10 pr-3 py-2 border border-[var(--border-color)] rounded-lg bg-[var(--bg-primary)] text-[var(--text-primary)] focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>
          </div>

          {/* Tipe Customer */}
          <div>
            <label className="block text-sm font-medium text-[var(--text-secondary)] mb-1">
              Tipe Customer
            </label>
            <select
              name="type"
              value={formData.type}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-[var(--border-color)] rounded-lg bg-[var(--bg-primary)] text-[var(--text-primary)] focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              {customerTypes.map(type => (
                <option key={type.value} value={type.value}>
                  {type.label}
                </option>
              ))}
            </select>
          </div>

          {/* Status Customer */}
          <div>
            <label className="block text-sm font-medium text-[var(--text-secondary)] mb-1">
              Status
            </label>
            <select
              name="status"
              value={formData.status}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-[var(--border-color)] rounded-lg bg-[var(--bg-primary)] text-[var(--text-primary)] focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              {customerStatuses.map(status => (
                <option key={status.value} value={status.value}>
                  {status.label}
                </option>
              ))}
            </select>
          </div>

          {/* Nomor Telepon */}
          <div>
            <label className="block text-sm font-medium text-[var(--text-secondary)] mb-1">
              Nomor Telepon *
            </label>
            <div className="relative">
              <FiPhone className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-secondary)]" size={18} />
              <input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                placeholder="Contoh: 08123456789"
                className="w-full pl-10 pr-3 py-2 border border-[var(--border-color)] rounded-lg bg-[var(--bg-primary)] text-[var(--text-primary)] focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>
          </div>

          {/* Email */}
          <div>
            <label className="block text-sm font-medium text-[var(--text-secondary)] mb-1">
              Email
            </label>
            <div className="relative">
              <FiMail className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-secondary)]" size={18} />
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="Contoh: customer@email.com"
                className="w-full pl-10 pr-3 py-2 border border-[var(--border-color)] rounded-lg bg-[var(--bg-primary)] text-[var(--text-primary)] focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          {/* Alamat */}
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-[var(--text-secondary)] mb-1">
              Alamat
            </label>
            <div className="relative">
              <FiMapPin className="absolute left-3 top-3 text-[var(--text-secondary)]" size={18} />
              <textarea
                name="address"
                value={formData.address}
                onChange={handleChange}
                rows="2"
                placeholder="Alamat lengkap customer"
                className="w-full pl-10 pr-3 py-2 border border-[var(--border-color)] rounded-lg bg-[var(--bg-primary)] text-[var(--text-primary)] focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          {/* Catatan */}
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-[var(--text-secondary)] mb-1">
              Catatan
            </label>
            <div className="relative">
              <FiFileText className="absolute left-3 top-3 text-[var(--text-secondary)]" size={18} />
              <textarea
                name="notes"
                value={formData.notes}
                onChange={handleChange}
                rows="2"
                placeholder="Catatan tambahan tentang customer"
                className="w-full pl-10 pr-3 py-2 border border-[var(--border-color)] rounded-lg bg-[var(--bg-primary)] text-[var(--text-primary)] focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>
        </div>

        <div className="flex items-center space-x-4 mt-6">
          <button
            type="submit"
            disabled={loading}
            className="flex items-center space-x-2 px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition-colors disabled:opacity-50"
          >
            <FiSave size={20} />
            <span>{loading ? 'Saving...' : isEdit ? 'Update' : 'Create'} Customer</span>
          </button>
          
          <button
            type="button"
            onClick={() => navigate('/customers')}
            className="flex items-center space-x-2 px-6 py-2 bg-gray-500 hover:bg-gray-600 text-white font-medium rounded-lg transition-colors"
          >
            <FiX size={18} />
            <span>Cancel</span>
          </button>
        </div>
      </form>
    </div>
  );
};

export default CustomerForm;